/*
  What the bar shows for a person (rebuild package 1B). Two parts, both read from the screens table (screens.ts):
  - the view tabs (`VIEW_TABS`): each opens the first of its targets the person may open, and is left out when there is none;
  - the More menu: every older screen that has a `nav` label, in its group (`NAV_GROUPS`), then the screens that stand alone.
  A person sees only what their role opens. Nothing here knows what a view shows: a second product adds its own tabs and screens
  to the same table.
*/

import { matchPath } from "react-router-dom";

import type { MeView } from "../api/types";
import { allows } from "./access";
import { NAV_GROUPS, SCREENS, VIEW_TABS, findScreen, groupOfPath, type Screen, type ViewId } from "./screens";

export interface NavItem {
  label: string;
  to: string;
  /** Match the address exactly (only Home), else the screens below it count too (a chapter under Chapters). */
  end: boolean;
}

/** A tab of the bar, with the address it opens for this person. */
export interface ViewTab {
  id: ViewId;
  label: string;
  to: string;
}

/** A group of the More menu. `label` is null for the screens that stand alone (Admin). */
export interface MoreGroup {
  id: string;
  label: string | null;
  summary?: string;
  items: NavItem[];
}

export interface Navigation {
  tabs: ViewTab[];
  more: MoreGroup[];
}

function itemOf(screen: Screen): NavItem {
  return { label: screen.nav ?? screen.title, to: screen.path, end: screen.path === "/" };
}

/** The tabs of the bar for this person, left to right. */
export function viewTabsFor(me: MeView): ViewTab[] {
  const tabs: ViewTab[] = [];
  for (const tab of VIEW_TABS) {
    const target = tab.targets.find((path) => {
      const screen = SCREENS.find((entry) => entry.path === path);
      return screen !== undefined && allows(me, screen.access);
    });
    if (target !== undefined) tabs.push({ id: tab.id, label: tab.label, to: target });
  }
  return tabs;
}

/** The groups of the More menu for this person, in the order the menu draws them. A group with nothing to open is left out. */
export function moreFor(me: MeView): MoreGroup[] {
  const visible = SCREENS.filter((screen) => screen.nav && allows(me, screen.access));
  const byPath = new Map(visible.map((screen) => [screen.path, screen]));

  const groups: MoreGroup[] = [];
  for (const group of NAV_GROUPS) {
    const items = group.paths.flatMap((path) => {
      const screen = byPath.get(path);
      return screen ? [itemOf(screen)] : [];
    });
    if (items.length > 0) groups.push({ id: group.id, label: group.label, summary: group.summary, items });
  }
  const alone = visible.filter((screen) => !groupOfPath(screen.path)).map(itemOf);
  if (alone.length > 0) groups.push({ id: "alone", label: null, items: alone });
  return groups;
}

export function navigationFor(me: MeView): Navigation {
  return { tabs: viewTabsFor(me), more: moreFor(me) };
}

/** The tab a screen is under, or null for a screen no tab holds. */
export function viewOfPath(pathname: string): ViewId | null {
  return findScreen(pathname)?.view ?? null;
}

/** Whether an address is the screen of an item (or lies below it). */
export function isCurrent(item: NavItem, pathname: string): boolean {
  return matchPath({ path: item.to, end: item.end }, pathname) !== null;
}

/**
 * How a tab marks the person's place: "page" on the screen the tab opens, "true" on another screen under the tab (an item page
 * under Items), and nothing elsewhere.
 */
export function tabCurrent(tab: ViewTab, pathname: string): "page" | "true" | undefined {
  if (matchPath({ path: tab.to, end: true }, pathname)) return "page";
  return viewOfPath(pathname) === tab.id ? "true" : undefined;
}

/** Whether the More menu holds the screen the person is on, so its button can say so. A screen under a tab belongs to the tab. */
export function moreHoldsCurrent(more: readonly MoreGroup[], pathname: string): boolean {
  if (viewOfPath(pathname) !== null) return false;
  return more.some((group) => group.items.some((item) => isCurrent(item, pathname)));
}
